import { Box, CircularProgress, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Branch, configRepository } from '../../const'
import Card from '../common/Card'
import {
  getAvailableTag,
  getBranches,
  getBranchingModel,
  getDiffStat,
  getFeaturesJSON,
  mergeBranches
} from '../../Controller'
import {
  APIResponse,
  Indicator,
  MergeProperties,
  Result,
  RepositoryMergeProperties,
  RepositoryProperties,
  StatusLine,
  MergeOperation,
  FeatureJsonProperties,
  FeatureJsonComponent
} from '../../types'
import * as lodash from 'lodash'
import './Merge.css'
import { MergeControl } from './ui/topControls/MergeControl'
import { MergeTableComponent } from './ui/table/MergeTableComponent'

export function Merge() {
  const [repositories, setRepositories] = useState([] as RepositoryMergeProperties[])
  const [toMerge, setToMerge] = useState([] as MergeProperties[])
  const [errorIndicator, setErrorIndicator] = useState([] as Indicator<string>[])
  const [mergeStatus, setMergeStatus] = useState({ message: '', fetching: false, failed: false } as StatusLine)
  const [fetching, setFetching] = useState(true)
  useEffect(() => {
    if (repositories.length > 0 && toMerge.length > 0 && errorIndicator.length > 0) {
      setFetching(false)
    }
  }, [repositories, toMerge, errorIndicator])

  useEffect(() => {
    async function getMergeInformation(repositoryInformation: RepositoryMergeProperties[]): Promise<MergeProperties[]> {
      const diffStatsRequests: Promise<Result<boolean> | APIResponse>[] = []
      const availableTagRequests: Promise<Result<string> | APIResponse>[] = []
      repositoryInformation.forEach((component, index) => {
        if (component.productionBranch.length <= 0) {
          repositoryInformation[index].productionBranch =
            component.branches.find(branch => branch !== component.developmentBranch) || Branch.master || Branch.main
        }
        if (component.developmentBranch.length <= 0) {
          repositoryInformation[index].developmentBranch =
            component.branches.find(branch => branch !== component.productionBranch) || Branch.master || Branch.main
        }
        diffStatsRequests.push(
          getDiffStat(
            component.repository,
            repositoryInformation[index].developmentBranch,
            repositoryInformation[index].productionBranch
          )
        )
        availableTagRequests.push(getAvailableTag(component.repository, repositoryInformation[index].productionBranch))
      })
      const diffStatsResponses = await Promise.all(diffStatsRequests)
      const availableTagResponses = await Promise.all(availableTagRequests)
      const mergeInformation = repositoryInformation.map((component, index) => {
        const defaultMergeInformation = {
          source: component.developmentBranch,
          destination: component.productionBranch,
          title: getMergeTitleText(component.developmentBranch, component.productionBranch),
          close: false
        }
        let diffStat = {
          diff: false,
          include: false
        }
        let message = ''
        if (diffStatsResponses[index].success) {
          diffStat = {
            diff: diffStatsResponses[index].result,
            include: diffStatsResponses[index].result
          }
        }
        if (availableTagResponses[index].success) {
          message = availableTagResponses[index].result
        }
        return {
          ...defaultMergeInformation,
          ...diffStat,
          message: message
        }
      })
      return mergeInformation
    }

    //initialization

    async function init() {
      const components = await getAllComponents()
      const newRepositoryMergeInformation = await getRepositoryMergeInformation(components)
      setRepositories(newRepositoryMergeInformation)
      const newMergeInformation = await getMergeInformation(newRepositoryMergeInformation)
      setToMerge(newMergeInformation)
      const newErrorIndicator = newMergeInformation.map(mergeInformation => {
        return {
          fetching: false,
          value: ''
        }
      })
      setErrorIndicator(newErrorIndicator)
    }
    init()
  }, [])

  function getMergeTitleText(source: string, destination: string): string {
    return 'Merged ' + source + ' in ' + destination
  }

  async function getRepositoryMergeInformation(
    components: RepositoryProperties[]
  ): Promise<RepositoryMergeProperties[]> {
    const branchingModelRequests = components.map(component => {
      return getBranchingModel(component.repository)
    })
    const branchingModelResponses = await Promise.all(branchingModelRequests)
    const newState = branchingModelResponses
      .map((response, index) => {
        if (response.success) {
          return {
            ...components[index],
            ...response.result
          } as RepositoryMergeProperties
        }
      })
      .filter(component => component) as RepositoryMergeProperties[]
    return newState
  }

  async function getAllComponents(): Promise<RepositoryProperties[]> {
    let allComponents: RepositoryProperties[] = []
    const configBranches = await getBranches(configRepository)
    if (configBranches.success) {
      const featureJsonRequests: Promise<Result<FeatureJsonProperties> | APIResponse>[] = configBranches.result.map(
        (branch: string) => {
          return getFeaturesJSON(branch)
        }
      )
      const featureJsonResponses = await Promise.all(featureJsonRequests)
      featureJsonResponses.forEach(response => {
        if (response.success) {
          response.result.components.forEach((component: FeatureJsonComponent, index: number) => {
            //no duplicate
            if (allComponents.every(compare => compare.repository !== component.repository)) {
              allComponents.push({
                branches: [],
                name: component.name,
                repository: component.repository
              } as RepositoryProperties)
            }
          })
        }
      })
    }
    const branchesRequests = allComponents.map(component => {
      return getBranches(component.repository)
    })
    const branchesResponses = await Promise.all(branchesRequests)
    // only return components with more then 1 branch
    const toReturn = branchesResponses
      .map((response, index) => {
        if (response.success && response.result.length > 1) {
          return {
            ...allComponents[index],
            branches: response.result
          }
        }
      })
      .filter(component => component) as RepositoryProperties[]
    return toReturn
  }

  //change states

  function setMessage(title: string, message: string) {
    setToMerge(prevState => {
      return prevState.map(state => {
        state.title = title
        state.message = message
        return state
      })
    })
  }

  function onMergeChange(operation: MergeOperation, index: number, value: string | boolean) {
    switch (operation) {
      case 'CHANGE_SOURCE':
        if (typeof value === 'string') {
          changeSource(value, index)
        }
        break
      case 'CHANGE_DESTINATION':
        if (typeof value === 'string') {
          changeDestination(value, index)
        }
        break
      case 'CHANGE_CLOSE':
        if (typeof value === 'boolean') {
          changeClose(value, index)
        }
        break
      case 'CHANGE_INCLUDE':
        if (typeof value === 'boolean') {
          changeInclude(value, index)
        }
        break
      default:
        break
    }
  }

  async function changeDestination(branch: string, index: number) {
    const newMessage = await getAvailableTag(repositories[index].repository, branch)
    const sourceBranch = branch === toMerge[index].source ? toMerge[index].destination : toMerge[index].source
    const diff = await getDiffStat(repositories[index].repository, sourceBranch, branch)
    setToMerge(prevState => {
      const newState = lodash.cloneDeep(prevState)
      if (diff.success) {
        newState[index].diff = diff.result
        newState[index].include = diff.result
      }
      newState[index].source = sourceBranch
      newState[index].destination = branch
      newState[index].message = newMessage.result
      newState[index].close = !isDevelopOrProduction(index, branch)
      newState[index].title = getMergeTitleText(sourceBranch, branch)
      return newState
    })
  }

  async function changeSource(branch: string, index: number) {
    const destinationBranch = branch === toMerge[index].destination ? toMerge[index].source : toMerge[index].destination
    const diff = await getDiffStat(repositories[index].repository, branch, destinationBranch)
    setToMerge(prevState => {
      const newState = lodash.cloneDeep(prevState)
      if (diff.success) {
        newState[index].diff = diff.result
        newState[index].include = diff.result
      }
      newState[index].destination = destinationBranch
      newState[index].source = branch
      newState[index].close = !isDevelopOrProduction(index, branch)
      newState[index].title = getMergeTitleText(branch, destinationBranch)
      return newState
    })
  }

  function changeClose(value: boolean, index: number) {
    setToMerge(prevState => {
      const newState = lodash.cloneDeep(prevState)
      newState[index].close = value
      return newState
    })
  }

  function isDevelopOrProduction(index: number, branch: String) {
    return repositories[index].productionBranch === branch || repositories[index].developmentBranch === branch
  }

  function changeInclude(value: boolean, index: number) {
    setToMerge(prevState => {
      const newState = lodash.cloneDeep(prevState)
      newState[index].include = value
      return newState
    })
  }

  //handle merge

  async function createMerge() {
    setMergeStatus({ message: 'Creating Pull Request', fetching: true, failed: false })
    const newErrorIndicator = errorIndicator.map((indicator, index) => {
      if (toMerge[index].include) {
        indicator.fetching = true
      }
      return indicator
    })
    setErrorIndicator(newErrorIndicator)
    const requests = toMerge
      .filter(merge => merge.include)
      .map((merge, index) => {
        return mergeBranches(
          repositories[index].repository,
          toMerge[index].source,
          toMerge[index].destination,
          toMerge[index].title,
          toMerge[index].message,
          toMerge[index].close
        )
      })
    const responses = await Promise.all(requests)
    if (responses.every(response => response.success)) {
      setMergeStatus(prevState => {
        const newState = lodash.cloneDeep(prevState)
        newState.message = 'Successfully merged pull requests'
        newState.fetching = false
        newState.failed = false
        return newState
      })
      setErrorIndicator(
        errorIndicator.map(state => {
          state.fetching = false
          return state
        })
      )
    } else {
      const newErrorIndicator = errorIndicator.map(state => {
        state.fetching = false
        return state
      })
      const errors = responses.filter(response => !response.success)
      errors.forEach(error => {
        const index = repositories.findIndex(component => component.repository === error.result.request.data.repository)
        if (index !== -1) {
          newErrorIndicator[index].value = error.result.message
        }
      })
      setMergeStatus(prevState => {
        const newState = lodash.cloneDeep(prevState)
        newState.message = 'Failed to merge all requests'
        newState.fetching = false
        newState.failed = true
        return newState
      })
      setErrorIndicator(newErrorIndicator)
    }
  }

  if (fetching) {
    return (
      <Card title='Merge' elevation={5}>
        <Box className='centerSpinner'>
          <CircularProgress className='spinner' size={60} />{' '}
          <Typography variant='subtitle1'>Fetching Data...</Typography>
        </Box>
      </Card>
    )
  } else {
    return (
      <Card title='Merge' elevation={5}>
        <MergeControl mergeStatus={mergeStatus} onCreateMerge={createMerge} onMergeChange={setMessage} />
        <MergeTableComponent
          mergeInformation={toMerge}
          repositories={repositories}
          onMergeChange={onMergeChange}
          errorIndicator={errorIndicator}
        />
      </Card>
    )
  }
}

export default Merge
